import { DefaultBlogImage } from 'components/_common/Icons';
import ArrowLineSvg from 'components/_common/Icons/SVGIcons/ArrowLineSvg';
import DotGridSvg from 'components/_common/Icons/SVGIcons/DotGridSvg';
import PlusMenuSvg from 'components/_common/Icons/SVGIcons/PlusMenuSvg';
import LazyImageWrapper from 'components/_common/LazyImageWrapper';
import { placeParamsToURL } from 'helpers/CommonHelper';
import { IMAGE_SIZE, getImageURL } from 'helpers/FileUploadHelper';
import { comingSoonAlert } from 'helpers/SwalAlertHelper';
import { t } from 'i18next';
import React, { useState, useRef, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { customerMainRoutes } from 'routes/customer/CustomerRoutes';
import { megaMenuCategoriesSelector } from 'store/customer/selectors/CustomerGlobalSelectors';
import { fetchHeaderFooterData } from 'store/customer/slices/CustomerGlobalSlice';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { PRODUCT_FILTER_QUERY_KEYS } from 'utils/productQuery';

import SellYourFirearmSection from '../Sections/SellYourFirearmSection';

interface Category {
  id: string;
  name: string;
  slug: string;
  image?: string;
  description?: string | null;
  children?: Category[];
  featured_manufacturers?: { name: string; slug: string; logo?: string }[];
  is_firearm_category?: boolean;
  is_featured?: boolean;
  is_comingsoon_item?: boolean;
}

const chunkArray = (arr: Category[], size: number): Category[][] => {
  const res: Category[][] = [];
  for (let i = 0; i < arr.length; i += size) {
    res.push(arr.slice(i, i + size));
  }
  return res;
};

const MegaMenuSection: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const allCategories = useAppSelector(megaMenuCategoriesSelector);
  // only featured categories appear in the navigation bar
  const categoryData = allCategories.filter((item: any) => item.is_featured);

  const [activeItem, setActiveItem] = useState<Category | null>(null);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [prevPathname, setPrevPathname] = useState(location.pathname);
  const [prevSearch, setPrevSearch] = useState(location.search);

  useEffect(() => {
    dispatch(fetchHeaderFooterData());
  }, [dispatch]);

  if (prevPathname !== location.pathname || prevSearch !== location.search) {
    setPrevPathname(location.pathname);
    setPrevSearch(location.search);
    setActiveItem(null);
  }

  const handleEnter = (item: Category) => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    setActiveItem(item);
  };

  const handleLeave = () => {
    timeoutRef.current = setTimeout(() => {
      setActiveItem(null);
    }, 150);
  };

  // a coming-soon category opens this popup instead of its menu - from its name and its plus alike
  const showComingSoon = (item: Category) =>
    comingSoonAlert(
      t('category_coming_soon_msg', {
        category: item.name,
        defaultValue: `${item.name} category is coming soon.`,
      })
    );

  if (!categoryData || categoryData.length === 0) return null;

  const chunks = chunkArray(activeItem?.children ?? [], 5);

  return (
    <div className="mega-menu-section" onMouseLeave={handleLeave}>
      <div className="w-100 d-flex justify-content-between align-items-center gap-3">
        <nav className="mega-menu-section__nav">
          <div className="mega-menu-section__item" onMouseEnter={() => setActiveItem(null)}>
            <Link
              to={placeParamsToURL(customerMainRoutes.productList.path)}
              className="mega-menu-section__link"
            >
              {t('shop_all')}
            </Link>
          </div>

          {categoryData.map((item: any) => (
            <div
              key={item.id}
              className="mega-menu-section__item"
              onMouseEnter={() => {
                if (item.is_comingsoon_item) {
                  setActiveItem(null);
                } else {
                  handleEnter(item);
                }
              }}
            >
              <button
                className={`mega-menu-section__link ${activeItem?.id === item.id ? 'active' : ''}`}
                onClick={(e) => {
                  if (item.is_comingsoon_item) {
                    e.preventDefault();
                    showComingSoon(item);
                  }
                }}
              >
                {item.is_comingsoon_item ? (
                  item.name
                ) : item.children?.length === 0 ? (
                  <Link
                    to={`${placeParamsToURL(customerMainRoutes.productList.path)}?${PRODUCT_FILTER_QUERY_KEYS.CATEGORY}=${item.slug}`}
                  >
                    {item.name}
                  </Link>
                ) : (
                  item.name
                )}
                {item.children?.length ? (
                  <span
                    className="plus-icon"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      // coming soon: same popup as the name (the click no longer reaches the button)
                      if (item.is_comingsoon_item) {
                        showComingSoon(item);
                        return;
                      }
                      // the plus turns into an X while the menu is open; clicking it closes the menu
                      if (timeoutRef.current) clearTimeout(timeoutRef.current);
                      setActiveItem(activeItem?.id === item.id ? null : item);
                    }}
                  >
                    <PlusMenuSvg />
                  </span>
                ) : null}
              </button>
            </div>
          ))}
        </nav>

        {activeItem?.children?.length ? (
          <div
            className="mega-dropdown"
            onMouseEnter={() => {
              if (timeoutRef.current) clearTimeout(timeoutRef.current);
            }}
          >
            <div className="mega-dropdown__left">
              <div className="mega-dropdown__left-title">
                <DotGridSvg />
                <h2>{activeItem.name}</h2>
              </div>
              <p className="mega-dropdown__left-desc">{activeItem?.description}</p>
            </div>

            <div className="mega-dropdown__divider" />
            <div className="mega-dropdown__middle">
              <div className="mega-dropdown__lists">
                {chunks.map((group, i) => (
                  <React.Fragment key={i}>
                    {i > 0 && <div className="mega-dropdown__divider" />}
                    <ul className="mega-dropdown__list">
                      {i === 0 ? (
                        <li className="mega-dropdown__list-item">
                          <Link
                            to={`${placeParamsToURL(customerMainRoutes.productList.path)}?${PRODUCT_FILTER_QUERY_KEYS.CATEGORY}=${activeItem.slug}`}
                          >
                            <span className="arrow-icon">
                              <ArrowLineSvg />
                            </span>
                            {t('all')}
                          </Link>
                        </li>
                      ) : (
                        <li
                          className="mega-dropdown__list-item placeholder-item"
                          style={{ pointerEvents: 'none', userSelect: 'none', opacity: 0 }}
                          aria-hidden="true"
                        >
                          <a>
                            <span className="arrow-icon">
                              <ArrowLineSvg />
                            </span>
                            &nbsp;
                          </a>
                        </li>
                      )}
                      {group.map((child) => (
                        <li key={child.id} className="mega-dropdown__list-item">
                          {child.is_comingsoon_item ? (
                            <span
                              className="mega-dropdown__coming-soon"
                              onClick={(e) => {
                                e.preventDefault();
                                showComingSoon(child);
                              }}
                            >
                              <span className="arrow-icon">
                                <ArrowLineSvg />
                              </span>
                              {child.name}
                            </span>
                          ) : (
                            <Link
                              to={`${placeParamsToURL(customerMainRoutes.productList.path)}?${PRODUCT_FILTER_QUERY_KEYS.SUB_CATEGORY}=${child.slug}${
                                child?.is_firearm_category === false
                                  ? `&${PRODUCT_FILTER_QUERY_KEYS.CATEGORY}=${activeItem.slug}`
                                  : ''
                              }`}
                            >
                              <span className="arrow-icon">
                                <ArrowLineSvg />
                              </span>
                              {child.name}
                            </Link>
                          )}
                        </li>
                      ))}
                    </ul>
                  </React.Fragment>
                ))}
              </div>
            </div>

            <div className="mega-dropdown__right">
              <LazyImageWrapper
                src={activeItem.image}
                showOriginalImage
                defaultPlaceholderImage={DefaultBlogImage}
                alt={activeItem.name}
                className="mega-dropdown__right-img"
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  display: 'block',
                }}
              />
              {activeItem.featured_manufacturers?.length ? (
                <div className="mega-dropdown__brands">
                  {activeItem.featured_manufacturers.map((brand, idx) =>
                    brand.logo ? (
                      <LazyImageWrapper
                        key={idx}
                        src={brand.logo}
                        renderImageSize={IMAGE_SIZE.md}
                        alt={brand.name}
                        width={'auto'}
                        height={48}
                        className="mega-dropdown__brand-logo"
                        onClick={() => {
                          navigate(
                            `${placeParamsToURL(customerMainRoutes.productList.path)}?${PRODUCT_FILTER_QUERY_KEYS.MANUFACTURER}=${brand.slug}`
                          );
                        }}
                      />
                    ) : (
                      <Link
                        to={`${placeParamsToURL(customerMainRoutes.productList.path)}?${PRODUCT_FILTER_QUERY_KEYS.MANUFACTURER}=${brand.slug}`}
                        key={idx}
                        className="mega-dropdown__brand-text"
                      >
                        {brand.name}
                      </Link>
                    )
                  )}
                </div>
              ) : null}
            </div>
          </div>
        ) : null}

        <SellYourFirearmSection />
        {/* Hidden preloader for mega menu images and brand logos to ensure they reflect immediately on hover */}
        <div style={{ display: 'none' }} aria-hidden="true">
          {categoryData.map((item: any) => (
            <React.Fragment key={item.id}>
              {item.image && <img src={getImageURL(item.image, IMAGE_SIZE.md)} alt="" />}
              {item.featured_manufacturers?.map((brand: any, idx: number) =>
                brand.logo ? (
                  <img key={idx} src={getImageURL(brand.logo, IMAGE_SIZE.md)} alt="" />
                ) : null
              )}
            </React.Fragment>
          ))}
        </div>
      </div>
    </div>
  );
};

export default MegaMenuSection;
